"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import Swal from "sweetalert2"
import { CirclesWithBar } from "react-loader-spinner"
import { getCompanies, verifyCompany, rejectCompany, toggleCompanyStatus } from "../../api"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.voyagian.com"

// Color palette for initials avatar
const AVATAR_COLORS = [
  "#dc3545", // red
  "#17a2b8", // teal/cyan
  "#ffc107", // yellow/gold
  "#28a745", // green
  "#6f42c1", // purple
  "#fd7e14", // orange
  "#20c997", // mint
  "#e83e8c", // pink
]

// Get consistent color based on company name
const getAvatarColor = (name) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export default function CompanyCardsGrid() {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [imageErrors, setImageErrors] = useState({}) // Track failed images
  const [togglingId, setTogglingId] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 1,
  })
  const [statusFilter, setStatusFilter] = useState("") // "", "pending", "approved", "rejected"

  useEffect(() => {
    fetchCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, statusFilter])

  const fetchCompanies = async () => {
    try {
      setLoading(true)
      const response = await getCompanies(pagination.page, pagination.limit, statusFilter)
      console.log("[v0] CompanyCardsGrid API Response:", response)

      if (response.success) {
        // Handle shape: { success, data: { companies, pagination } }
        const companiesData = response.data?.companies || response.data || []
        const paginationData = response.data?.pagination || response.pagination

        console.log("[v0] Companies data:", companiesData)
        if (companiesData.length > 0) {
          console.log("[v0] First company logoUrl:", companiesData[0].logoUrl)
          console.log("[v0] First company logo:", companiesData[0].logo)
        }

        setCompanies(companiesData)
        if (paginationData) {
          setPagination((prev) => ({
            ...prev,
            ...paginationData,
          }))
        }
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to fetch companies",
      })
    } finally {
      setLoading(false)
    }
  }

  // Handle verify company
  const handleVerify = async (companyId, companyName) => {
    const result = await Swal.fire({
      title: "Send Verification Link?",
      text: `Send verification link to "${companyName}"'s email?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Send Link",
      cancelButtonText: "Cancel",
    })

    if (result.isConfirmed) {
      try {
        const response = await verifyCompany(companyId)
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Link Sent!",
            text: response.message || "Verification link sent to company email.",
            timer: 3000,
            showConfirmButton: false,
          })
          fetchCompanies()
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to send verification link",
        })
      }
    }
  }

  // Handle reject company
  const handleReject = async (companyId, companyName) => {
    const result = await Swal.fire({
      title: "Reject Company?",
      text: `Are you sure you want to reject "${companyName}"?`,
      icon: "warning",
      input: "textarea",
      inputLabel: "Rejection Reason (optional)",
      inputPlaceholder: "Enter reason for rejection...",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reject",
      cancelButtonText: "Cancel",
    })

    if (result.isConfirmed) {
      try {
        const response = await rejectCompany(companyId, result.value || "")
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Rejected!",
            text: response.message || "Company has been rejected.",
            timer: 2000,
            showConfirmButton: false,
          })
          fetchCompanies()
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to reject company",
        })
      }
    }
  }

  // Handle toggle company status
  const handleToggleStatus = async (companyId, companyName, currentStatus) => {
    const newStatus = !currentStatus
    setTogglingId(companyId)

    try {
      const response = await toggleCompanyStatus(companyId, newStatus)
      if (response.success) {
        // Update the company in local state
        setCompanies((prev) =>
          prev.map((company) => (company._id === companyId ? { ...company, isActive: newStatus } : company)),
        )

        Swal.fire({
          icon: "success",
          title: newStatus ? "Enabled!" : "Disabled!",
          text: response.message || `Company has been ${newStatus ? "enabled" : "disabled"}.`,
          timer: 2000,
          showConfirmButton: false,
        })
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to toggle company status",
      })
    } finally {
      setTogglingId(null)
    }
  }

  // Get company initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 3)
  }

  // Get status badge
  const getStatusBadge = (status, verificationPending) => {
    switch (status) {
      case "approved":
        // If you want it to say "Active" like the design:
        return <span className="badge bg-success status-badge">Active</span>
      case "rejected":
        return <span className="badge bg-danger status-badge">Rejected</span>
      case "pending":
      default:
        if (verificationPending) {
          return <span className="badge bg-info text-white status-badge">Link Sent</span>
        }
        return <span className="badge bg-warning text-dark status-badge">Pending</span>
    }
  }

  const handleImageError = (companyId, logoUrl) => {
    console.log("[v0] Image failed to load for company:", companyId)
    console.log("[v0] Failed logo URL:", logoUrl)
    setImageErrors((prev) => ({ ...prev, [companyId]: true }))
  }

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return null
    if (logoUrl.startsWith("http")) return logoUrl
    const fullUrl = `${API_BASE_URL}${logoUrl}`
    console.log("[v0] Constructed logo URL:", fullUrl)
    return fullUrl
  }

  if (loading) {
    return (
      <div className="text-center py-5 d-flex flex-column align-items-center justify-content-center">
        <CirclesWithBar
          height="100"
          width="100"
          color="#05468f"
          outerCircleColor="#05468f"
          innerCircleColor="#05468f"
          barColor="#05468f"
          ariaLabel="circles-with-bar-loading"
          visible={true}
        />
        <p className="mt-2">Loading companies...</p>
      </div>
    )
  }

  if (companies.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No companies found.</p>
      </div>
    )
  }

  return (
    <>
      {/* Status Filter Bar (top right, small, no theme change) */}
      <div className="d-flex justify-content-end mb-3">
        <div className="d-flex align-items-center">
          <label className="form-label mb-0 me-2">Status:</label>
          <select
            className="form-select form-select-sm"
            style={{ maxWidth: 180 }}
            value={statusFilter}
            onChange={(e) => {
              const value = e.target.value
              // Reset to first page when filter changes
              setPagination((prev) => ({ ...prev, page: 1 }))
              setStatusFilter(value)
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="row g-4">
        {companies.map((company) => {
          const logoField = company.logoUrl || company.logo
          console.log("[v0] Card Company:", company.companyName, "Logo field:", logoField)

          return (
            <div className="col-md-3" key={company._id}>
              <div className="card p-3 card-purple text-center">
                {/* Logo / Initials */}
                {logoField && !imageErrors[company._id] ? (
                  <img
                    src={getLogoUrl(logoField) || "/placeholder.svg"}
                    alt={company.companyName}
                    onError={() => handleImageError(company._id, getLogoUrl(logoField))}
                    crossOrigin="anonymous"
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      objectFit: "cover",
                      margin: "0 auto 10px",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      backgroundColor: getAvatarColor(company.companyName),
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      fontSize: "16px",
                      margin: "0 auto 10px",
                    }}
                  >
                    {getInitials(company.companyName)}
                  </div>
                )}

                {/* Title & location */}
                <h6 className="mb-1">{company.companyName}</h6>
                <p className="text-muted mb-1">{company.country || "N/A"}</p>

                {/* Status badge */}
                {getStatusBadge(company.status, company.verificationPending)}

                {/* Toggle switch for approved companies */}
                {company.status === "approved" && (
                  <div className="mt-2 d-flex align-items-center justify-content-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                      {company.isActive ? "Enabled" : "Disabled"}
                    </span>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={company.isActive !== false}
                        onChange={() =>
                          handleToggleStatus(company._id, company.companyName, company.isActive !== false)
                        }
                        disabled={togglingId === company._id}
                        style={{ cursor: togglingId === company._id ? "wait" : "pointer" }}
                      />
                    </div>
                  </div>
                )}

                {/* Button like "Edit" in your design */}
                <div className="mt-3">
                  {company.status === "pending" ? (
                    <>
                      <button
                        className="btn btn-sm btn-success me-1"
                        onClick={() => handleVerify(company._id, company.companyName)}
                      >
                        Verify
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleReject(company._id, company.companyName)}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <Link to={`/companies/${company._id}`} className="btn btn-sm btn-primary">
                      Edit
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.page === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(pagination.pages)].map((_, index) => (
              <li key={index + 1} className={`page-item ${pagination.page === index + 1 ? "active" : ""}`}>
                <button className="page-link" onClick={() => setPagination((prev) => ({ ...prev, page: index + 1 }))}>
                  {index + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${pagination.page === pagination.pages ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  )
}
